import { Component } from '@angular/core';
import { MqttConnectionState, MqttService } from 'ngx-mqtt';
import { Subject } from 'rxjs';
import { AuthService } from './services/auth.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  title = 'SadinHub-UI';

  protected ngUnsubscribe$: Subject<void> = new Subject<void>();

  private mqttReconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private mqttStarted = false;
  private mqttPlannedDisconnect = false;

  constructor(
    private _mqttService: MqttService,
    private _authService: AuthService,
  ) {}
  //#endregion

  //#region Input & Output

  //#endregion

  //#region lifecycle hook Methods
  ngOnInit(): void {
    this.connectMqtt();
    this._mqttService.state.subscribe((state) => {
    console.log('[MQTT] Connection state:', MqttConnectionState[state]);

    if (
      state === MqttConnectionState.CLOSED ) {
      this.mqttStarted = false;
    }
  });

    this._mqttService.onError.subscribe((error) => {
      console.error('[MQTT] Error:', error);
      this.mqttStarted = false;

      if (!this.mqttPlannedDisconnect) {
        this.scheduleMqttReconnect('error');
      }
    });

    this._mqttService.onClose.subscribe(() => {
      const wasPlannedDisconnect = this.mqttPlannedDisconnect;

      console.warn('[MQTT] Socket closed', {
        planned: wasPlannedDisconnect,
        clientId: sessionStorage.getItem('mqttUser'),
        at: new Date().toISOString(),
      });

      this.mqttStarted = false;
      this.mqttPlannedDisconnect = false;

      if (!wasPlannedDisconnect) {
        this.scheduleMqttReconnect('unexpected-close');
      }
    });

    this._mqttService.onReconnect.subscribe(() => {
      console.warn('[MQTT] Reconnecting...');
    });



    this._authService.mqttConnection$.subscribe((e) => {
      if (e['refresh']) {
        console.log('[MQTT] Refresh signal received, reconnecting...');

        this.safeDisconnectMqtt('refresh-signal');

        this.clearMqttReconnectTimeout();
        this.mqttReconnectTimeout = setTimeout(() => {
          this.connectMqtt(e['jwt']);
        }, 500);
      }

      if (e['disconnect']) {
        console.log('[MQTT] Disconnect signal received, disconnecting...');
        this.clearMqttReconnectTimeout();
        this.safeDisconnectMqtt('disconnect-signal');
      }
    });
  }

  connectMqtt(jwtOverride?: string): void {
    if (this.mqttStarted) {
      console.warn('[MQTT] connectMqtt skipped because MQTT already started');
      return;
    }

    const user = this._authService.getCurrentUser();
    if (!user) {
      this.mqttStarted = false;
      return;
    }

    const jwt = jwtOverride || user?.jwt;
    if (!jwt) {
      this.mqttStarted = false;
      return;
    }

    if (this._authService.isTokenExpired(jwt)) {
      this.mqttStarted = false;
      this.connectMqttAfterTokenRefresh(user);
      return;
    }

    this.mqttStarted = true;

    const mqttClientId = this.getMqttClientId(user);

    this._mqttService.connect({
      username: 'WEBuser_'+jwt,
      clientId: mqttClientId,
      password: jwt,
    });
  }

  ngOnDestroy(): void {
    this.clearMqttReconnectTimeout();
    this.safeDisconnectMqtt('app-destroy');
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }


  private safeDisconnectMqtt(reason: string): void {
    console.trace('[MQTT] Disconnect requested:', {
      reason,
      clientId: sessionStorage.getItem('mqttUser'),
      at: new Date().toISOString(),
    });

    this.mqttPlannedDisconnect = true;

    try {
      this._mqttService.disconnect();
    } catch (error) {}

    this.mqttStarted = false;
  }

  private clearMqttReconnectTimeout(): void {
    if (this.mqttReconnectTimeout) {
      clearTimeout(this.mqttReconnectTimeout);
      this.mqttReconnectTimeout = null;
    }
  }

  private connectMqttAfterTokenRefresh(user: any): void {
    if (!user?.jwt_refresh || this._authService.isTokenExpired(user.jwt_refresh)) {
      console.warn('[MQTT] Access and refresh tokens are expired; clearing session');
      this._authService.clearExpiredSession();
      return;
    }

    console.warn('[MQTT] Access token expired before MQTT connect; refreshing first');
    this._authService.refreshToken().subscribe({
      next: (jwt) => {
        if (jwt) {
          this.connectMqtt(jwt);
          return;
        }

        this._authService.clearExpiredSession();
      },
      error: () => {
        this._authService.clearExpiredSession();
      }
    });
  }

  private scheduleMqttReconnect(reason: string): void {
    if (!localStorage.getItem('currentUser')) {
      return;
    }

    this.clearMqttReconnectTimeout();
    this.mqttReconnectTimeout = setTimeout(() => {
      console.warn('[MQTT] Reconnecting after unexpected disconnect:', {
        reason,
        clientId: sessionStorage.getItem('mqttUser'),
        at: new Date().toISOString(),
      });

      this.connectMqtt();
    }, 2000);
  }

  private getMqttClientId(user?: any): string {
    let mqttClientId = sessionStorage.getItem('mqttUser');

    if (!mqttClientId) {
      const username = this.getMqttClientName(user);
      const randomNumber = Math.floor(100000 + Math.random() * 900000);
      mqttClientId = `WEBclient_${username}_${randomNumber}`;
      sessionStorage.setItem('mqttUser', mqttClientId);
    }

    return mqttClientId;
  }

  private getMqttClientName(user?: any): string {
    const fullName = `${user?.first_name || ''}${user?.last_name || ''}`;
    return this.sanitizeMqttClientName(fullName || localStorage.getItem('mqttClientName') || user?.email);
  }

  private sanitizeMqttClientName(username: string): string {
    return String(username)
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '_') || 'anonymous';
  }

}
